/**
 * Separately sold modules, as the frontend sees them.
 *
 * The backend decides; this only reads its answer from `GET /api/modules`. A module is usable when
 * it is both **licensed** (bought, set by the vendor) and **active** (switched on by the shop
 * administrator). When it is not, its menu entry stays visible but greyed out, as a hint that the
 * module exists. See `documentation/implementation_plan_e_invoice_module.md` section 4.
 */

/** A module's state, exactly as `GET /api/modules` reports it. */
export interface ModuleState {
    key: string;
    licensed: boolean;
    active: boolean;
    available: boolean;
}

/** Why a module cannot be used. */
export type ModuleUnavailableReason = 'notLicensed' | 'notActive';

/** Translation keys of the modules this frontend knows, by module key. */
export const MODULE_TITLE_KEYS: Readonly<Record<string, string>> = {
    'e-invoice': 'modules.eInvoice.title',
};

/** The display-name translation key of a module; an unknown one falls back to its key. */
export function moduleTitleKey(key: string): string {
    return MODULE_TITLE_KEYS[key] ?? key;
}

/**
 * Why `key` cannot be used, or `null` when it can.
 *
 * A module missing from the list - not loaded yet, or the request failed - counts as not licensed.
 * Failing closed costs a licensed shop a moment of a greyed entry while the list loads; failing
 * open would send everyone else to a page whose every call is refused.
 */
export function moduleUnavailableReason(
    modules: readonly ModuleState[],
    key: string,
): ModuleUnavailableReason | null {
    const state = modules.find(module => module.key === key);

    if (state?.available === true) {
        return null;
    }

    return state?.licensed === true ? 'notActive' : 'notLicensed';
}

/** The translation key explaining `reason` to the user. */
export function moduleUnavailableMessageKey(reason: ModuleUnavailableReason): string {
    return `modules.${reason}`;
}

/** Whether the administrator's switch can be changed: never for a module that is not licensed. */
export function canToggleModule(state: ModuleState): boolean {
    return state.licensed;
}
